import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X } from "lucide-react";

/**
 * Rebrand Announcement Splash (First-load overlay)
 * - Shows once on first landing page visit (per browser session)
 * - Click X / Scroll button / outside → dismiss, then user sees the regular landing page
 * - Does NOT push the regular HeroSection down — overlays on top
 */
export const RebrandHeroSection = () => {
  // Only show on the very first landing visit per session
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem("nc_rebrand_splash_seen") !== "true";
    } catch {
      return true;
    }
  });

  const dismiss = () => {
    try { sessionStorage.setItem("nc_rebrand_splash_seen", "true"); } catch {}
    setVisible(false);
  };

  // Lock body scroll while splash is open
  useEffect(() => {
    if (visible) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] overflow-hidden"
          data-testid="rebrand-hero"
        >
          {/* Background image */}
          <img
            src="https://customer-assets.emergentagent.com/job_9ded4c02-97ba-4f9a-8fba-246dcb30d447/artifacts/fmxna4ae_ChatGPT%20Image%20May%2014%2C%202026%2C%2004_07_33%20PM.png"
            alt="리즈TV가 뉴커플링으로 새롭게 시작되었습니다"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center center" }}
          />

          {/* Soft warm gradient overlay for text legibility */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(255,243,230,0.0) 25%, rgba(40,15,30,0.35) 55%, rgba(20,8,20,0.75) 100%)",
            }}
          />

          {/* Close button */}
          <button
            type="button"
            onClick={dismiss}
            aria-label="닫기"
            className="absolute top-5 right-5 md:top-8 md:right-8 z-50 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md flex items-center justify-center text-white transition border border-white/20"
            data-testid="rebrand-close-btn"
          >
            <X className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Bottom announcement text */}
          <div className="absolute inset-x-0 bottom-0 pb-12 md:pb-20 px-6">
            <div className="max-w-7xl mx-auto text-center">
              <motion.h1
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, ease: "easeOut", delay: 0.6 }}
                className="font-sans text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.15] md:leading-[1.1] whitespace-nowrap"
                style={{ textShadow: "0 4px 28px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.5)" }}
                data-testid="rebrand-headline"
              >
                <span className="block">
                  <span className="text-rose-200">리즈TV</span>가
                  <span className="text-rose-100"> 뉴커플링</span>으로
                </span>
                <span className="block mt-2 md:mt-4">새롭게 시작되었습니다</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0, ease: "easeOut", delay: 1.0 }}
                className="mt-6 md:mt-8 text-lg md:text-2xl lg:text-3xl text-white/90 max-w-4xl mx-auto font-light"
                style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
              >
                중년의 우아한 인연을 위한 글로벌 한인 중년 커뮤니티
                <br className="hidden md:block" />
                <span className="opacity-80">— 더 따뜻하게, 더 진중하게, 더 가깝게.</span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.0, delay: 1.6 }}
                className="mt-10 md:mt-12 flex items-center justify-center"
              >
                <button
                  type="button"
                  onClick={dismiss}
                  className="group inline-flex flex-col items-center gap-2 text-white/85 hover:text-white transition"
                  aria-label="홈으로 들어가기"
                  data-testid="rebrand-enter-btn"
                >
                  <span className="text-xs md:text-sm uppercase tracking-[0.25em] font-light">
                    Enter Site
                  </span>
                  <ChevronDown className="w-5 h-5 md:w-6 md:h-6 animate-bounce" />
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RebrandHeroSection;
